<template>
  <div class="p-1">
    <div class="d-flex align-items-center justify-content-between">
      <h2 class="text-capitalize mr-2">{{ pageTitle }}</h2>
      <div class="d-flex align-items-center">
        <button class="ml-2 btn btn-outline-primary" @click="handleClearGridFilters">
          Clear Filters
        </button>
      </div>
    </div>
    <DxDataGrid
      :columnChooser="columnChooser"
      :key="currentLab"
      @cell-click="handleCaseClick"
      :data-source="accessionDataStore"
      noDataText="Please apply filters to search cases."
      :show-borders="true"
      :show-row-lines="true"
      :column-auto-width="true"
      :scrolling="scrolling"
      :allow-column-reordering="true"
      :remoteOperations="remoteOperations"
      :allow-column-resizing="true"
      columnResizingMode="widget"
      @content-ready="onContentReady"
      @option-changed="onOptionChanged"
      @initialized="onInitialized"
      :onCellPrepared="redactLogRocket"
      :toolbar="toolbar"
      :pager="pager"
      :paging="paging"
      :hoverStateEnabled="true"
      :sorting="sorting"
      :headerFilter="headerFilter"
      :filterRow="filterRow"
      :selection="selection"
      :stateStoring="stateStoring"
      :searchPanel="searchPanel"
      :cacheEnabled="false"
    >
      <DxColumn v-for="(column, i) of columns" :key="i" v-bind="column" />
      <template v-slot:actionsTemplate="{ data: { data, key, rowIndex } }">
        <div
          class="justify-content-start d-flex align-items-center"
          :data-testid="'actions-col-' + rowIndex"
        >
          <router-link
            v-if="permissions.CaseView"
            v-tooltip.right="'View case details.'"
            :to="getModeRoute(key) || '/'"
            class="btn-link btn p-0"
          >
            <icon class="text-primary" icon="eye" />
          </router-link>
          <icon-button
            v-if="permissions.PathReportView"
            v-tooltip.right="'View path report.'"
            class="btn text-primary pointer p-0 px-1 mr-1"
            @click="handleViewPathReport(key)"
            icon="window-restore"
          />
          <icon-button
            v-if="isCaseReadyForSignout(data)"
            @click="handleSignCase(data)"
            v-tooltip.right="signButton(data).message"
            :icon="signButton(data).icon"
            class="btn text-primary pointer p-0 px-1 mr-1"
            :disabled="isLoading"
          />
          <icon-button
            v-if="canFinalizeCase(data)"
            @click="handleFinalizeCases([data])"
            v-tooltip.right="'Finalize Case'"
            icon="check"
            class="btn text-primary pointer p-0 px-1 mr-1"
            :disabled="isLoading"
          />
        </div>
      </template>
      <template v-slot:booleanValue="{ data }">
        <icon v-if="data.value" class="text-success" icon="check" />
      </template>
    </DxDataGrid>
    <modal :status="isDistributionOpen" @close="isDistributionOpen = false">
      <distribution-popup
        @close="isDistributionOpen = false"
        @submitted="clearSelection"
        :method="distributionMethod"
        :caseIds="selectedCasesForDistribution"
      />
    </modal>
    <modal :status="isLocationPopupOpen" @close="isLocationPopupOpen = false">
      <lab-location-popup
        :isLoading="isLoading"
        @cancel="isLocationPopupOpen = false"
        @sign="handleSelectLabLocation"
        :payloadToSign="payloadToSign"
      />
    </modal>
    <modal :status="isCollectedPopupOpen" @close="isCollectedPopupOpen = false">
      <collected-popup
        @submit="handleSetCollected"
        @cancel="isCollectedPopupOpen = false"
        :caseId="collectedPopupCaseId"
      />
    </modal>
    <PathReportViewer v-if="isInline && isActivated" class="mt-2" @close="closeReport" />
  </div>
</template>

<script>
import CaseService from "../services/cases";
import { mapGetters, mapState } from "vuex";
import { parseISO, format } from "date-fns";
import eventBus, {
  SIGNED_CASE_FROM_PDF,
  fromBusEvent,
  FILTER_ACCESSION_GRID,
  SIGN_CASE_ON_GRID
} from "@/modules/eventBus";
import { DxDataGrid, DxColumn } from "devextreme-vue/data-grid";
import PathReportViewer from "./Reports/PathReportViewer.vue";
import Reports from "../services/Reports";
import { combineLatest, fromEvent } from "rxjs";
import {
  calculateAccessionNumbering,
  createLogItem,
  caseCreationMethodEnum,
  createQueryArray,
  formatDatetimeCell,
  createLocalTzDateFilter,
  addCommasToCount,
  checkIfProxy,
  calculateNameFilter,
  caseGridAccessionNumberFilter,
  formatSSN,
  isCaseSigned,
  getPdfFileName
  // batchRedirect
} from "../modules/helpers";
import pathReportMessages from "../services/pathReportMessages";
import {
  tap,
  map,
  filter,
  switchMap,
  withLatestFrom,
  throttleTime,
  distinctUntilChanged
} from "rxjs/operators";
import AuditLog from "../services/AuditLog";
import { CasesApi, DropdownApi } from "../services/index";
import DistributionPopup from "./DistributionPopup.vue";
import Modal from "./common/Modal.vue";
import IconButton from "./common/IconButton.vue";
import Icon from "./common/Icon.vue";
import CustomStore from "devextreme/data/custom_store";
import {
  ACCESSION_MODES,
  AuditLogItems,
  CaseStatusEnum,
  CYTOLOGY_STATUSES,
  DashboardModes,
  DistributionMethodsEnum
} from "@/modules/enums";
import api from "@/services/api";
import { handleErrors } from "@/modules/handleErrors";
import LabLocationPopup from "./LabLocationPopup.vue";
import ScannerDetection from "@/modules/scanner";
import { scanCaseBarcode } from "@/modules/scanCaseBarcode";
import CollectedPopup from "./CollectedPopup.vue";
import { AuditLogApi } from "@/services";
import { getModeRoute } from "@/modules/getModeRoute";

export default {
  name: "All-Cases-Grid",
  components: {
    DxDataGrid,
    PathReportViewer,
    DistributionPopup,
    Modal,
    IconButton,
    Icon,
    DxColumn,
    LabLocationPopup,
    CollectedPopup
  },
  data() {
    return {
      isLoading: false,
      isDistributionOpen: false,
      selectedCasesForDistribution: [],
      distributionMethod: "",
      directQueryProperties: ["accessionNumber", "orderNumber"],
      isFiltered: false,
      popupVisible: false,
      report: {},
      priorities: [],
      grid: {},
      pathReport: {},
      searchPanel: {
        width: 300,
        visible: true,
        placeholder: "Search by Case Number"
      },
      filterRow: {
        visible: true,
        applyFilter: "auto"
      },
      scrolling: {
        showScrollbar: "always",
        useNative: true
      },
      selection: {
        mode: "multiple",
        allowSelectAll: true,
        showCheckBoxesMode: "always",
        selectAllMode: "page"
      },
      headerFilter: {
        visible: true
      },
      sorting: {
        mode: "multiple"
      },
      remoteOperations: {
        filtering: true,
        sorting: true,
        paging: true
      },
      accessionGrid: {},
      grouping: {
        allowCollapsing: true
      },
      pager: {
        allowedPageSizes: [10, 15, 20, 25, 50, 100],
        showInfo: true,
        infoText: "Page {0} of {1} ({2} Cases)",
        showPageSizeSelector: true,
        visible: true,
        displayMode: "full"
      },
      paging: {
        enabled: true
      },
      isLocationPopupOpen: false,
      payloadToSign: null,
      scanner: {},
      billingCycles: [],
      isCollectedPopupOpen: false,
      collectedPopupCaseId: null,
      isActivated: true,
      lastSearchTime: null,
      hasContentReadyHappened: false
    };
  },
  domStreams: [
    "contentReady$",
    "optionChanged$",
    "activated$",
    "deactivate$",
    "initialized$",
    "saveState$"
  ],
  subscriptions() {
    const currentRow$ = this.contentReady$.pipe(map(({ component }) => component.getVisibleRows()));
    const firstContentReady$ = this.contentReady$;
    const showFirstRowPathReport$ = this.contentReady$.pipe(
      throttleTime(5000),
      map(({ component }) => {
        return component.getKeyByRowIndex(0);
      }),
      filter(caseId => {
        const paramCaseId = this.$route.params.caseId;
        if (paramCaseId) {
          return paramCaseId === caseId;
        }
        return this.isActivated && caseId && caseId !== this.reportCaseId;
      }),
      tap(caseId => {
        this.handleViewPathReport(caseId);
      })
    );
    //Returns the searchPanel input element.
    const searchPanel$ = firstContentReady$.pipe(
      filter(({ element }) => element.querySelector('input[type="text"]')),
      map(({ element }) => {
        const searchPanel = element.querySelector('input[type="text"]');
        if (searchPanel && !this.hasContentReadyHappened) {
          this.hasContentReadyHappened = true;
          searchPanel.setAttribute("id", "searchPanel");
          searchPanel.focus();
        }
        return searchPanel;
      })
    );
    const enterInSearchPanel$ = searchPanel$.pipe(
      switchMap(searchPanel => {
        return fromEvent(searchPanel, "keydown").pipe(
          distinctUntilChanged(),
          filter(event => event.keyCode === 13),
          map(event => {
            return { searchValue: event.target.value, timeStamp: event.timeStamp };
          })
        );
      })
    );
    const quickSearch$ = combineLatest([
      //This event will only dispatch when there is 1 or 0 row visible.
      currentRow$.pipe(
        filter(rows => rows.length === 2),
        distinctUntilChanged(
          (prev, curr) => prev[0]?.data.accessionNumber === curr[0]?.data.accessionNumber
        )
      ),
      //This event is being fired when enter is pressed.
      enterInSearchPanel$
    ]).pipe(
      filter(data => data[1] !== this.lastSearchTime),
      tap(([data, event]) => {
        const { searchValue, timeStamp } = event;
        this.lastSearchTime = timeStamp;
        if (data?.length === 1) {
          this.handleDirectQuery(data[0].data, searchValue);
          console.log("Case matched with %s", searchValue);
        }
      })
    );

    const searchAny$ = combineLatest([
      //This event will only dispatch when there is 1 or 0 row visible.
      currentRow$.pipe(filter(rows => rows.length === 0)),
      //This event is being fired when enter is pressed.
      enterInSearchPanel$
    ]).pipe(
      filter(data => data[1].timeStamp !== this.lastSearchTime),
      tap(([data, event]) => {
        const { searchValue, timeStamp } = event;
        this.lastSearchTime = timeStamp;
        if (data?.length === 0) {
          this.searchAnyCase(searchValue);
        }
      })
    );

    const listenForFocusSearch$ = searchPanel$.pipe(
      switchMap(searchPanel => {
        return fromBusEvent(SIGNED_CASE_FROM_PDF).pipe(
          filter(() => this.isActivated),
          tap(() => {
            this.refreshGrid();
            searchPanel.focus();
            searchPanel.setSelectionRange(0, searchPanel.value.length);
          })
        );
      })
    );
    const focusSearch$ = this.activated$.pipe(
      withLatestFrom(searchPanel$),
      tap(data => {
        const [value, searchPanel] = data;
        searchPanel.focus();
        if (value) {
          searchPanel.setSelectionRange(0, searchPanel.value.length);
          this.refreshGrid();
        }
      })
    );
    const showSignoutQueue$ = fromBusEvent(FILTER_ACCESSION_GRID).pipe(
      withLatestFrom(firstContentReady$),
      tap(([filters, grid]) => {
        grid.clearFilter();
        filters.forEach(values => {
          grid.columnOption(...values);
        });
      })
    );

    return {
      showFirstRowPathReport$,
      searchPanel$,
      quickSearch$,
      searchAny$,
      listenForFocusSearch$,
      focusSearch$,
      showSignoutQueue$
    };
  },
  mounted() {
    DropdownApi.getBillingCycles().then(res => {
      this.billingCycles = res || [];
    });
  },
  activated() {
    this.handleOnActivated();
  },
  deactivated() {
    this.isActivated = false;
    if (this.scanner?.stopScanning) {
      this.scanner.stopScanning();
    }
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser,
      reportViewer: state => state.report,
      pathReportLocation: state => state.applicationSettings.pathReportLocation,
      currentLab: state => state.currentLab,
      labSettings: state => state.labSettings,
      systemInformation: state => state.systemInformation,
      accessionFilter: state => state.applicationSettings.accessionFilter,
      accessionMode: state => state.applicationSettings.accessionMode,
      casesSearchHeader: state => state.applicationSettings.casesSearchHeader,
      casesGridState: state => state.grids.allCases,
      isMobileView: state => state.isMobileView,
      AccessionNumberingType: state => state.labSettings.AccessionNumberingType,
      currentLabLocation: state => state.currentLabLocation,
      availableLabLocations: state => state.availableLabLocations,
      reportCaseId: state => state.report.caseId,
      homeGridMode: state => state.applicationSettings.homeGridMode,
      useZip: state => state.labSettings.DownloadMultiplePathReportsAsZipFile,
      nextCaseSource: state => state.accessionStore.nextCaseSource,
      warnIfProxy: state => state.applicationSettings.warnIfProxy,
      cytologyModuleEnabled: state => state.labSettings.CytologyModuleEnabled,
      dashboardFilters: state => state.sessionDetails.dashboardFilters,
      dashboardProviderDisplay: state => state.applicationSettings.dashboardProviderDisplay
    }),
    ...mapGetters(["permissions"]),
    ...mapGetters("report", ["isInline"]),
    caseStatuses() {
      const allStatuses = this.$store.state.caseStatuses || [];
      if (this.cytologyModuleEnabled) {
        return allStatuses;
      }
      return allStatuses.filter(status => !CYTOLOGY_STATUSES.includes(status));
    },
    stateStoring() {
      return {
        enabled: true,
        type: "custom",
        customLoad: this.customStateLoad,
        customSave: this.customStateSave,
        ignoreColumnOptionNames: ["filterValue", "filterValues", "headerFilter", "filterType"]
      };
    },
    toolbar() {
      return {
        visible: true,
        items: [
          {
            widget: "dxButton",
            visible: this.permissions.CaseView,
            options: {
              icon: "fa fa-file-excel",
              onClick: this.exportToExcel,
              hint: "Click to export as excel file",
              elementAttr: {
                class: "icon-color"
              },
              text: "Export to Excel"
            },
            locateInMenu: "auto",
            showText: "inMenu"
          },
          {
            widget: "dxButton",
            visible: this.permissions.CaseView,
            options: {
              icon: "fa fa-download",
              onClick: this.handleDownloadCases,
              hint: "Click to download selected cases.",
              elementAttr: {
                class: "icon-color"
              },
              text: "Download"
            },
            locateInMenu: "auto",
            showText: "inMenu"
          },
          {
            widget: "dxButton",
            visible: this.permissions.CaseSignout || this.permissions.CaseSignoutByProxy,
            options: {
              icon: "fa fa-pen",
              onClick: this.handleSignMultipleCase,
              hint: "Click Sign Out selected cases.",
              elementAttr: {
                class: "icon-color",
                disabled: this.isLoading
              },
              text: "Sign Out"
            },
            locateInMenu: "auto",
            showText: "inMenu"
          },
          {
            widget: "dxButton",
            visible: this.permissions.CaseFinalizeSlidePrep,
            options: {
              icon: "fa fa-check",
              onClick: this.handleFinalizeCases,
              hint: "Click to finalize selected cases.",
              elementAttr: {
                class: "icon-color"
              },
              text: "Finalize"
            },
            locateInMenu: "auto",
            showText: "inMenu"
          },
          {
            widget: "dxButton",
            visible: this.permissions.PathReportSendByPrint,
            options: {
              icon: "fa fa-print",
              onClick: () => this.handleDistributeCases(DistributionMethodsEnum.Print),
              hint: "Click to print selected cases.",
              elementAttr: {
                class: "icon-color"
              },
              text: "Print"
            },
            locateInMenu: "auto",
            showText: "inMenu"
          },
          {
            widget: "dxButton",
            visible: this.permissions.PathReportSendByFax,
            options: {
              icon: "fa fa-fax",
              onClick: () => this.handleDistributeCases(DistributionMethodsEnum.Fax),
              hint: "Click to fax selected cases.",
              elementAttr: {
                class: "icon-color"
              },
              text: "Fax"
            },
            locateInMenu: "auto",
            showText: "inMenu"
          },
          {
            widget: "dxButton",
            visible: this.permissions.PathReportSendByEmail,
            options: {
              icon: "fa fa-envelope",
              onClick: () => this.handleDistributeCases(DistributionMethodsEnum.Email),
              hint: "Click to email selected cases.",
              elementAttr: {
                class: "icon-color"
              },
              text: "Email"
            },
            locateInMenu: "auto",
            showText: "inMenu"
          },
          {
            widget: "dxButton",
            visible: this.permissions.PathReportSendByFileDrop,
            options: {
              icon: "fa fa-file-pdf",
              onClick: () => this.handleDistributeCases(DistributionMethodsEnum.FileDrop),
              hint: "Click to file drop selected cases.",
              elementAttr: {
                class: "icon-color"
              },
              text: "File Drop"
            },
            locateInMenu: "auto",
            showText: "inMenu"
          },
          {
            widget: "dxButton",
            visible: this.permissions.PathReportSendByHL7,
            options: {
              icon: "fa fa-share-alt",
              onClick: () => this.handleDistributeCases(DistributionMethodsEnum.HL7),
              hint: "Click to use hl7 on selected cases.",
              elementAttr: {
                class: "icon-color"
              },
              text: "Use HL7"
            },
            locateInMenu: "auto",
            showText: "inMenu"
          },
          {
            name: "columnChooserButton",
            visible: true
          },
          {
            name: "searchPanel",
            visible: true
          }
        ]
      };
    },
    columnChooser() {
      return {
        enabled: true,
        allowSearch: true,
        mode: "select"
      };
    },
    pageTitle() {
      return `${this.accessionFilter || ""} Cases`;
    },
    isReportOpen() {
      return this.reportViewer.isOpen;
    },
    isProvider() {
      const { isLabClientUser } = this.currentUser;
      if (isLabClientUser) {
        return true;
      }
      return false;
    },
    accessionDataStore() {
      const { procedures, casePrefixTags, holdCodes, pathologists, alertFilter, tags } =
        this.casesSearchHeader;
      let url = `/api/Cases/search`;
      const queryParams = [];
      if (this.accessionMode === ACCESSION_MODES.AwaitingScreening) {
        queryParams.push("isCytology=true");
      }
      if (procedures?.length) {
        queryParams.push(createQueryArray(procedures, "procedures"));
      }
      if (casePrefixTags?.length) {
        queryParams.push(createQueryArray(casePrefixTags, "casePrefixTags"));
      }
      if (holdCodes?.length) {
        queryParams.push(createQueryArray(holdCodes, "holdCodes"));
      }
      if (pathologists?.length) {
        queryParams.push(createQueryArray(pathologists, "pathologists"));
      }
      if (tags?.length) {
        queryParams.push(createQueryArray(tags, "tags"));
      }
      if (alertFilter) {
        queryParams.push(`alertFilter=${alertFilter}`);
      }
      if (queryParams.length) {
        url = `${url}?${queryParams.join("&")}`;
      }
      return new CustomStore({
        key: "caseId",
        load(options) {
          if (Array.isArray(options?.filter) && options.filter?.length) {
            const store = api.createSearch(url, "caseId");
            return store.load(options);
          }
          return new Promise(res => res({ data: [], totalCount: 0 }));
        }
      });
    },
    columns() {
      const priorityStore = new CustomStore({
        key: ["id", "displayName"],
        load() {
          return DropdownApi.getPriorities();
        }
      });
      const columns = [
        {
          type: "buttons",
          cellTemplate: "actionsTemplate",
          caption: "Action",
          width: "100px"
        },
        {
          dataField: "caseCreationMethodId",
          caption: "Method",
          lookup: {
            dataSource: caseCreationMethodEnum,
            displayExpr: "displayName",
            valueExpr: "id"
          },
          dataType: "number",
          allowSearch: false
        },
        {
          dataField: "priority",
          dataType: "string",
          allowSearch: false,
          allowSorting: true,
          calculateSortValue: "prioritySort",
          lookup: {
            dataSource: priorityStore,
            displayExpr: "displayName",
            valueExpr: "displayName"
          }
        },
        {
          dataField: "caseStatus",
          dataType: "string",
          defaultFilterValues: [],
          showInColumnChooser: false,
          allowFiltering: true,
          lookup: {
            dataSource: this.caseStatuses.map((e, i) => ({ displayName: e, id: i })),
            displayExpr: "displayName",
            valueExpr: "id"
          },
          caption: "Status",
          allowSearch: false
        },
        {
          dataField: "numberYear",
          caption: "Year",
          alowSearch: false
        },
        {
          dataField: "numberSequence",
          dataType: "number",
          caption: "Number",
          allowSearch: false
        },
        {
          dataField: "accessionNumber",
          allowReordering: true,
          caption: "Case #",
          allowHeaderFiltering: false,
          dataType: "string",
          sortOrder: "desc",
          sortIndex: 0,
          allowSearch: true,
          calculateFilterExpression: caseGridAccessionNumberFilter(this.AccessionNumberingType),
          selectedFilterValue: "="
        },
        {
          dataField: "patientName",
          allowReordering: true,
          dataType: "string",
          caption: "Name",
          allowHeaderFiltering: false,
          allowSearch: false,
          filterOperations: ["startswith"],
          calculateFilterExpression: value => calculateNameFilter(value)
        },
        {
          dataField: "patientDOB",
          allowReordering: true,
          dataType: "date",
          caption: "DOB",
          allowHeaderFiltering: false,
          allowSearch: false
        },
        {
          dataField: "patientAgeAtReceiptDisplay",
          allowReordering: true,
          caption: "Age",
          dataType: "string",
          allowSearch: false,
          allowSorting: false,
          allowHeaderFiltering: false,
          allowFiltering: false
        },
        {
          dataField: "patientSex",
          caption: "Gender",
          allowReordering: true,
          dataType: "number",
          width: "50px",
          lookup: {
            dataSource: [
              {
                text: "M",
                id: 0
              },
              {
                text: "F",
                id: 1
              },
              {
                text: "O",
                id: 2
              },
              {
                text: "U",
                id: 3
              }
            ],
            displayExpr: "text",
            valueExpr: "id"
          },
          allowHeaderFiltering: false,
          allowSearch: false
        },

        {
          dataField: "collectedOn",
          caption: "Collected",
          allowReordering: true,
          dataType: "date",
          allowHeaderFiltering: false,
          allowSearch: false
        },

        {
          dataField: "numberOfSpecimens",
          allowReordering: true,
          caption: "Quantity",
          dataType: "number",
          width: "50px",
          allowHeaderFiltering: false,
          allowSearch: false
        },
        {
          dataField: "patientMRN",
          allowReordering: true,
          caption: "MRN",
          dataType: "string",
          allowHeaderFiltering: false,
          allowFiltering: true,
          allowSearch: false
        },

        {
          dataField: "patientSSN",
          allowReordering: true,
          caption: "SSN",
          dataType: "string",
          visible: this.permissions.SocialSecurityNumberView,
          allowSorting: false,
          allowHeaderFiltering: false,
          allowSearch: false,
          allowFiltering: false,
          dataPrivate: "redact",
          calculateDisplayValue: data => {
            if (data.patientSSN) {
              return formatSSN(data.patientSSN);
            }
            return "";
          }
        },
        {
          dataField: "providerName",
          allowReordering: true,
          caption: "Provider",
          dataType: "string",
          allowSearch: false,
          calculateDisplayValue: data => {
            const { providerFirstName, providerLastName } = data;
            if (this.dashboardProviderDisplay === 1) {
              return `${providerLastName}, ${providerFirstName}`;
            }
            return `${providerFirstName} ${providerLastName}`;
          },
          calculateFilterExpression: value =>
            calculateNameFilter(value, "providerFirstName", "providerLastName"),
          calculateSortValue: "providerName"
        },
        {
          dataField: "providerOfficeName",
          caption: "Location",
          dataType: "string",
          allowReordering: true,
          allowSearch: false
        },
        {
          dataField: "providerAddress1",
          caption: "Loc. Address",
          dataType: "string",
          allowSearch: false
        },
        {
          dataField: "receivedOn",
          allowReordering: true,
          caption: "Received",
          dataType: "date",
          allowHeaderFiltering: false,
          allowSearch: false
        },
        {
          dataField: "reportedOn",
          caption: "Reported",
          allowReordering: true,
          dataType: "date",
          allowSearch: false,
          calculateDisplayValue: data => {
            if (data.reportedOn) {
              return this.calculateReportedOn(data.reportedOn);
            }
            return "";
          },
          calculateFilterExpression: (data, filterExpr) => {
            return createLocalTzDateFilter(data, filterExpr, "reportedOn");
          },
          calculateSortValue: "reportedOn"
        },
        {
          dataField: "orderNumber",
          filterField: "OrderNumber",
          allowReordering: true,
          dataType: "string",
          caption: "Order #",
          allowSearch: false
        },
        {
          dataField: "accountNumber",
          filterField: "AcctNumber",
          dataType: "string",
          allowReordering: true,
          caption: "Account Number",
          allowSearch: false
        },
        {
          dataField: "pathologistName",
          caption: "Pathologist",
          dataType: "string",
          allowSearch: false
        },
        {
          dataField: "billingCycle",
          caption: "Billing Cycle",
          dataType: "string",
          lookup: {
            dataSource: this.billingCycles,
            displayExpr: "displayName",
            valueExpr: "id"
          },
          allowSearch: false
        },
        {
          dataField: "hx",
          filterField: "hx",
          allowReordering: true,
          dataType: "boolean",
          cellTemplate: "booleanValue",
          allowHeaderFiltering: true,
          allowFiltering: false,
          caption: "HX",
          headerFilter: {
            dataSource: [
              {
                text: "True",
                value: ["hx", "=", true]
              },
              {
                text: "False",
                value: ["hx", "=", false]
              }
            ]
          },
          allowSearch: false
        },
        {
          dataField: "ins",
          allowHeaderFiltering: true,
          filterField: "Ins",
          caption: "INS",
          allowReordering: true,
          dataType: "boolean",
          cellTemplate: "booleanValue",
          allowFiltering: false,
          headerFilter: {
            dataSource: [
              {
                text: "True",
                value: ["ins", "=", true]
              },
              {
                text: "False",
                value: ["ins", "=", false]
              }
            ]
          },
          allowSearch: false
        },
        {
          dataField: "hasSlideImages",
          allowHeaderFiltering: true,
          filterField: "Slides",
          caption: "Slides",
          allowReordering: true,
          dataType: "boolean",
          cellTemplate: "booleanValue",
          allowFiltering: false,
          headerFilter: {
            dataSource: [
              {
                text: "True",
                value: ["hasSlideImages", "=", true]
              },
              {
                text: "False",
                value: ["hasSlideImages", "=", false]
              }
            ]
          },
          allowSearch: false
        }
      ];

      if (this.cytologyModuleEnabled) {
        columns.push({
          dataField: "hasMolecTests",
          filterField: "hasMolecTests",
          allowReordering: true,
          dataType: "boolean",
          cellTemplate: "booleanValue",
          allowHeaderFiltering: true,
          allowFiltering: false,
          caption: "Molec. Tests",
          headerFilter: {
            dataSource: [
              {
                text: "True",
                value: ["hasMolecTests", "=", true]
              },
              {
                text: "False",
                value: ["hasMolecTests", "=", false]
              }
            ]
          },
          allowSearch: false
        });
      }
      return columns;
    }
  },
  methods: {
    clearSelection() {
      if (this.grid.clearSelection) {
        this.grid.clearSelection();
      }
    },
    handleClearGridFilters() {
      this.grid.clearFilter("row");
      this.grid.clearFilter("header");
      this.grid.clearFilter("filterValue");
      this.grid.clearFilter("search");
      this.grid.clearFilter("dataSource");
    },
    handleDirectQuery(data, searchValue) {
      ["accessionNumber", "orderNumber"].forEach(property => {
        if (data[property]) {
          let rowValue = data[property].replace(/[a-z]/gi, "");
          if (property === "accessionNumber") {
            searchValue = calculateAccessionNumbering(searchValue, this.AccessionNumberingType);
            searchValue = searchValue.replace(/[a-z]/gi, "");
          }
          rowValue = RegExp.escape(rowValue || "");
          const targetRegex = new RegExp(rowValue, "i");
          if (targetRegex.test(searchValue)) {
            return this.handleNavigateToCase(data.caseId);
          }
        }
      });
    },
    focusElement(element) {
      this.$nextTick(() => element.focus({ preventScroll: true }));
    },
    onContentReady(event) {
      addCommasToCount();
      this.contentReady$?.next(event);
    },
    onInitialized(event) {
      this.grid = event.component;
      if (this.dashboardFilters?.allCases?.length) {
        setTimeout(() => {
          for (const column of this.dashboardFilters.allCases) {
            if (column?.name) {
              this.grid.columnOption(column.name, "filterValue", column.filterValue);
            }
          }
        }, 1000);
      }
      this.initialized$.next(event);
    },
    refreshGrid() {
      if (this.grid.refresh) {
        this.grid.refresh(true);
        this.grid.clearSelection();
      }
    },
    repaintGrid() {
      if (this.grid.repaint) {
        this.grid.repaint(true);
      }
    },
    onOptionChanged(event) {
      if (event?.fullName.includes("filterValue")) {
        this.saveFilters(event);
      }
      this.optionChanged$?.next(event);
    },
    isCaseReadyForSignout(data) {
      if (!data?.pathologistGUID) {
        return false;
      }
      if (this.currentUser.id !== data.pathologistGUID && !checkIfProxy(data.pathologistGUID)) {
        return false;
      }
      return (
        (this.permissions.CaseSignout || this.permissions.CaseSignoutByProxy) &&
        [6, 9, 12].includes(data.caseStatus)
      );
    },
    closeReport() {
      this.$store.commit("report/toggleReportViewer", false);
    },
    customStateSave(gridState) {
      const payload = {
        name: "allCases",
        gridState,
        columns: this.columns
      };
      this.$store.dispatch("grids/setGridState", payload);
    },
    customStateLoad() {
      return this.casesGridState;
    },
    handleViewPathReport(caseId) {
      if (this.isActivated) {
        this.$store.dispatch("report/viewPathReport", { caseId });
      }
    },
    async handleSignMultipleCase() {
      if (this.isLoading) {
        return;
      }
      this.payloadToSign = null;
      const selectedRows = this.grid.getSelectedRowsData();
      if (selectedRows.length === 0) {
        return window.notify("No case selected.", "error");
      }
      if (
        selectedRows.find(
          e => e.pathologistGUID !== this.currentUser.id && !checkIfProxy(e.pathologistGUID)
        )
      ) {
        window.alert(
          "User does not have permission to sign out one or more selected cases as primary pathologist or as proxy. Please check your selection and try again."
        );
        return;
      }
      if (this.availableLabLocations.length > 1 && !this.currentLabLocation) {
        this.isLocationPopupOpen = true;
        return;
      }
      const caseIds = selectedRows
        .filter(
          e =>
            [
              CaseStatusEnum.ResultedOnHold,
              CaseStatusEnum.Resulted,
              CaseStatusEnum.ResultedAgain,
              CaseStatusEnum.Signed
            ].includes(e.caseStatus) && e.collectedOn
        )
        .map(v => {
          return v.caseId;
        });
      if (
        this.labSettings.ConfirmPrelimSignOuts &&
        selectedRows.find(e => e.caseStatus === CaseStatusEnum.ResultedOnHold)
      ) {
        const confirm = await window.confirm(
          "One or more cases you are signing out has a hold code attached. Are you sure you want to sign these cases as prelim?"
        );
        if (!confirm) {
          return;
        }
      }
      const unsignableCases = selectedRows.filter(e => !caseIds.includes(e.caseId));
      try {
        this.isLoading = true;
        if (caseIds.length > 0) {
          document.body.style.cursor = "wait";
          const SignResponse = await CaseService.multiSignCase(caseIds, this.currentLabLocation);
          if (SignResponse && unsignableCases.length) {
            window.alert(
              `${
                caseIds.length
              } cases successfully signed<br><br>The following cases could not be signed:<br>${unsignableCases
                .map(e => e.accessionNumber)
                .join("<br>")}`
            );
            const logItem = createLogItem(
              SignResponse.length === 1 ? SignResponse[0] : {},
              AuditLogItems.ChangeAccession
            );
            logItem.comments = `Signed out case${
              SignResponse.length > 1 ? "s" : ""
            } ${SignResponse.map(e => e.caseNumber).join(", ")}.`;
            AuditLog.insertLogMessage(logItem);
          } else if (SignResponse) {
            window.notify(`${caseIds.length} cases successfully signed`);
            const logItem = createLogItem(
              SignResponse.length === 1 ? SignResponse[0] : {},
              AuditLogItems.ChangeAccession
            );
            logItem.comments = `Signed out case${
              SignResponse.length > 1 ? "s" : ""
            } ${SignResponse.map(e => e.caseNumber).join(", ")}.`;
            AuditLog.insertLogMessage(logItem);
          }
          document.body.style.cursor = "";
          this.refreshGrid();
          this.$nextTick(() => eventBus.$emit(SIGN_CASE_ON_GRID));
        } else {
          throw "Cases cannot be signed because they are not in the right status or do not have Collected On dates.";
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        this.isLoading = false;
        document.body.style.cursor = "";
      }
    },
    async handleSignCase(caseDetails) {
      if (this.isLoading) {
        return;
      }
      if (this.warnIfProxy) {
        const isProxy = checkIfProxy(caseDetails.pathologistGUID);
        if (isProxy) {
          const confirm = await window.confirm(
            "You are signing this case out as Proxy. Do you wish to continue?"
          );
          if (!confirm) {
            return;
          }
        }
      }
      this.payloadToSign = null;
      if (
        this.labSettings.ConfirmPrelimSignOuts &&
        caseDetails.caseStatus === CaseStatusEnum.ResultedOnHold
      ) {
        const confirm = await window.confirm(
          "This case has a hold code attached. Are you sure you want to sign it as prelim?"
        );
        if (!confirm) {
          return;
        }
      }
      try {
        this.isLoading = true;
        const { caseStatus, collectedOn, caseId } = caseDetails;
        if (this.canSignOut(caseDetails)) {
          if (this.availableLabLocations.length > 1 && !this.currentLabLocation) {
            this.isLocationPopupOpen = true;
            this.payloadToSign = caseDetails;
            return;
          }
          const isSigned = isCaseSigned(caseStatus);
          if (!isSigned && !collectedOn) {
            this.isCollectedPopupOpen = true;
            this.collectedPopupCaseId = caseId;
            return;
          } else {
            const caseId = caseDetails.caseId;
            document.body.style.cursor = "wait";
            const SignResponse = await this.$store.dispatch("accessionStore/handleSignCase", {
              caseId,
              labLocationId: this.currentLabLocation,
              caseStatus
            });
            if (SignResponse) {
              window.notify(
                `Successfully ${caseStatus === 10 ? "unsigned" : "signed"} accession ${
                  caseDetails.accessionNumber
                }. `
              );
              // if (SignResponse?.batchRedirect) {
              //   batchRedirect();
              // }
            }
            document.body.style.cursor = "";
            if (this.searchPanel$?.focus) {
              this.focusElement(this.searchPanel$);
            }
            this.refreshGrid();
          }
        } else {
          throw "Case cannot be signed with the current status.";
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        setTimeout(() => {
          this.isLoading = false;
        }, 500);
        document.body.style.cursor = "";
      }
    },
    async handleDownloadCase({ data }) {
      const response = Reports.getSSRSReport({ caseId: data.caseId, format: "PDF" });
      const url = URL.createObjectURL(response);
      var a = document.createElement("a");
      a.href = url;
      a.download = `${data.accessionNumber}_REPORT`;
      a.click();
    },
    exportToExcel() {
      let selectedRows = this.grid.getSelectedRowsData();
      if (selectedRows.length) {
        this.grid.exportToExcel(true);
      } else {
        selectedRows = this.grid.getVisibleRows().map(e => e.data);
        this.grid.exportToExcel();
      }
      const logItem = createLogItem({}, 8, "Accession Grid");
      logItem.comments =
        "Exported the following cases to excel " +
        selectedRows.map(e => e.accessionNumber).join(" \n ");
      AuditLog.insertLogMessage(logItem);
      return;
    },
    markAsViewed({ data }) {
      const { isViewed, caseId } = data;
      const userMeta = {
        isViewed: !isViewed,
        caseId
      };
      return CaseService.updateUserMeta(userMeta).then(this.grid.refresh);
    },
    markAll() {
      const data = this.grid.getSelectedRowsData();
      return Promise.all(data.map(e => this.markAsViewed({ data: e }))).then(this.grid.refresh);
    },
    getLocation(data) {
      const contactEntry = data?.contacts?.find(e => e.isPrimary);
      if (contactEntry) {
        const {
          contact: { displayName }
        } = contactEntry;
        return displayName || "N/A";
      }
      return "N/A";
    },
    getFullName(data) {
      return data.patientLastName + ", " + data.patientFirstName;
    },
    getStatus({ caseStatus }) {
      return this.caseStatuses[caseStatus] || "Other";
    },
    canSignOut(data) {
      const { caseStatus } = data;
      if (caseStatus === 6 || caseStatus === 9 || caseStatus === 12 || caseStatus === 10) {
        return true;
      }
      return false;
    },
    signButton(data) {
      const { caseStatus } = data;
      if (caseStatus === 6 || caseStatus === 9 || caseStatus === 12) {
        return { icon: "edit", message: "Sign accession." };
      }
      if (caseStatus === 10) {
        return { icon: "unlink", message: "Unsign accession" };
      }
      return { icon: "edit", message: "Sign accession." };
    },
    getDOB(data) {
      return data.patientDOB ? format(parseISO(data.patientDOB), "MM/dd/yyyy") : "";
    },
    getSex(data) {
      switch (data.patientSex) {
        case 0:
          return "M";
        case 1:
          return "F";
        default:
          return "";
      }
    },
    async handleDownloadCases() {
      const cases = this.grid.getSelectedRowsData();
      if (cases.length === 0) {
        return window.notify("No accessions selected.", "error");
      }
      const reportedCases = cases.filter(e =>
        [
          CaseStatusEnum.ReportedPrelim,
          CaseStatusEnum.Reported,
          CaseStatusEnum.ReReleased
        ].includes(e.caseStatus)
      );
      if (!reportedCases.length) {
        return window.notify(
          "No reported cases selected. Only cases in a reported state can be downloaded.",
          "error"
        );
      }
      if (cases.length > reportedCases.length) {
        const confirm = await window.confirm(
          "One or more selected cases is not in a reported status and cannot be downloaded. Do you still wish to download the reported cases?"
        );
        if (!confirm) {
          return;
        }
      }
      this.isLoading = true;

      try {
        const caseIds = reportedCases.map(e => e.caseId);
        const extension = this.useZip && caseIds.length > 1 ? "zip" : "pdf";
        const file = await CasesApi.getMultiCasePdf(caseIds, extension);
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        if (reportedCases.length === 1) {
          a.download = getPdfFileName(reportedCases[0]);
        } else {
          a.download = `${format(new Date(), "MM/dd/yyyy")}_PATH_REPORTS.${extension}`;
        }
        a.click();
        pathReportMessages.addPathReportMessage({
          caseIds: reportedCases.map(e => e.caseId),
          method: 6
        });
        const logItem = createLogItem({}, 9, "Accession Grid");
        logItem.comments =
          "Downloaded the following pdfs " + reportedCases.map(e => e.accessionNumber).join(" \n ");
        AuditLog.insertLogMessage(logItem);
      } catch (error) {
        window.notify("Error loading file.", "error");
      }
      this.isLoading = false;
    },

    async handleDistributeCases(method) {
      const cases = this.grid.getSelectedRowsData();
      if (cases.length === 0) {
        return window.notify("No accessions selected.", "error");
      }
      const allStatuses = cases.map(e => e.caseStatus);
      let signedCases = cases.filter(e => [7, 8, 10, 11, 13, 14].includes(e.caseStatus));
      const allCasesSigned = allStatuses.every(e =>
        [
          CaseStatusEnum.SignedOnHold,
          CaseStatusEnum.ReportedPrelim,
          CaseStatusEnum.SignedAgain,
          CaseStatusEnum.Reported,
          CaseStatusEnum.SignedAgain,
          CaseStatusEnum.Reported,
          CaseStatusEnum.Signed
        ].includes(e)
      );
      if (!allCasesSigned) {
        if (method === DistributionMethodsEnum.HL7) {
          if (!signedCases?.length) {
            window.alert("Cases that have not been signed cannot be distributed through HL7.");
            return;
          }
          const confirm = await window.confirm(
            `<div class="text-center">
              One or more of the selected accessions has not been signed out and cannot be distributed through HL7.<br>Do you wish to continue?
          </div>`
          );
          if (!confirm) {
            return;
          }
        } else {
          const confirm = await window.confirm(
            `<div class="text-center">
              One or more of the selected accessions has not been signed out. <br> Are you sure you want to distribute this report?
          </div>`
          );
          if (!confirm) {
            return;
          }
        }
      }
      this.selectedCasesForDistribution =
        method === DistributionMethodsEnum.HL7
          ? signedCases.map(e => e.caseId)
          : cases.map(e => e.caseId);
      this.distributionMethod = method;
      this.isDistributionOpen = true;
    },
    getCollectedOn(data) {
      return data.collectedOn ? format(parseISO(data.collectedOn), "MM/dd/yyyy") : "";
    },
    getReceiveOn(data) {
      return data.receivedOn ? format(parseISO(data.receivedOn), "MM/dd/yyyy") : "";
    },
    getReportedOn(data) {
      return data.reportedOn ? format(parseISO(data.reportedOn), "MM/dd/yyyy") : "";
    },
    getOrderNumber(data) {
      return data.orderNumber ? data.orderNumber : "";
    },
    getModeRoute(caseId) {
      if (this.$route.fullPath.includes("accession")) {
        return;
      }
      let route = "/";
      if (this.isMobileView) {
        route = {
          name: "CaseView",
          params: { caseId }
        };
        return;
      }
      route = getModeRoute(this.accessionMode, caseId);
      return route;
    },
    handleNavigateToCase(caseId) {
      if (this.$route.fullPath.includes("accession")) {
        return;
      }
      const route = this.getModeRoute(caseId);
      this.$router.push(route);
    },
    handleCaseClick(event) {
      //Stops the rerouting if you click inside the action or selection cell
      if (event?.column?.name === "buttons" || !event?.column?.name) {
        return;
      }
      if (event.data) {
        return this.handleNavigateToCase(event.data.caseId);
      }
    },
    calculateReportedOn(reportedOn) {
      if (reportedOn) {
        const formattedDate = formatDatetimeCell(reportedOn);
        return format(formattedDate, "M/dd/yyyy");
      }
      return "";
    },
    handleSelectLabLocation(data) {
      this.payloadToSign = null;
      this.isLocationPopupOpen = false;
      if (data?.caseId) {
        this.handleSignCase(data);
      } else {
        this.handleSignMultipleCase();
      }
    },
    async handleScanBarcode(barcode) {
      this.devlog("Scanned Barcode on All Cases Grid.", barcode);
      if (
        !this.isActivated ||
        this.$route.name !== "Home" ||
        this.homeGridMode !== DashboardModes.Cases
      ) {
        return;
      }
      const modalOpen = await document.getElementsByClassName("modal-mask");
      if (modalOpen.length) {
        return;
      }
      const { caseNumber, specimenOrder } = scanCaseBarcode(barcode);
      if (!caseNumber) {
        return;
      }
      if (
        specimenOrder &&
        [
          ACCESSION_MODES.Specimen,
          ACCESSION_MODES.Result,
          ACCESSION_MODES.AwaitingPathologist,
          ACCESSION_MODES.AwaitingReviewer,
          ACCESSION_MODES.AwaitingScreening
        ].includes(this.accessionMode)
      ) {
        this.$store.commit("accessionStore/setSpecimenToLoad", specimenOrder);
      }
      const matchingCases = await CasesApi.searchStore.load({
        filter: ["accessionNumber", caseNumber]
      });
      if (matchingCases.length === 1) {
        this.handleNavigateToCase(matchingCases[0].caseId);
      }
    },
    async handleSetCollected(collectedOnDate) {
      this.isCollectedPopupOpen = false;
      if (!this.collectedPopupCaseId) {
        window.alert("An error ocurred signing out this case. Please try again.");
      }
      const caseDetails = await CasesApi.getCaseById(this.collectedPopupCaseId);
      const updatedCase = await this.$store.dispatch("accessionStore/updateCaseDetails", {
        ...caseDetails,
        collectedOn: collectedOnDate
      });
      this.handleSignCase({
        ...updatedCase,
        caseStatus: updatedCase?.caseStatus || updatedCase?.status
      });
      this.collectedPopupCaseId = null;
    },
    async searchAnyCase(searchValue) {
      this.isLoading = true;
      let prefix = "";
      if (/[A-Z]{1,6}/i.test(searchValue)) {
        prefix = searchValue.match(/[A-Z]{1,6}/i)[0].toUpperCase();
      }
      const numberToSearch = calculateAccessionNumbering(searchValue, this.AccessionNumberingType);
      const caseMatches = await CasesApi.searchStore.load({
        filter: ["accessionNumber", prefix ? "=" : "contains", numberToSearch]
      });
      if (caseMatches.length === 1) {
        this.handleNavigateToCase(caseMatches[0].caseId);
      }
      this.isLoading = false;
    },
    handleOnActivated() {
      this.scanner = new ScannerDetection({
        onComplete: this.handleScanBarcode,
        stopPropogation: true,
        minLength: 4,
        timeBeforeScanTest: 250
      });
      this.isActivated = true;
      this.activated$.next(true);
    },
    async handleFinalizeCases(cases) {
      if (!Array.isArray(cases)) {
        cases = this.grid.getSelectedRowsData();
      }
      if (!cases?.length) {
        window.alert("Please select cases to finalize.");
        return;
      }
      this.isLoading = true;
      document.body.style.cursor = "wait";
      try {
        const caseIds = cases.map(e => e.caseId);
        const response = await CasesApi.finalizeCases(caseIds, this.currentLabLocation);
        if (!response?.length) {
          window.alert("Error finalizing cases.");
          return;
        }
        const errors = response.filter(e => !e.successful);
        const successfulCases = response.filter(e => e.successful);
        if (errors?.length) {
          let message = "";
          const successfulCount = successfulCases?.length;
          if (successfulCount) {
            message = `Successfully finalized ${successfulCount} cases.<br><br>`;
          }
          if (errors?.length > 1) {
            message += "The following cases were unable to be finalized:<br>";
          }
          message += errors
            .map(e => (errors.length > 1 ? e.caseNumber + ": " : "") + e.errorMessage)
            .join("<br>");
          window.alert(message);
          if (!successfulCount) {
            return;
          }
        } else {
          window.notify(
            `Finalized ${
              response.length > 1 ? response.length + " cases" : response[0].caseNumber
            }.`
          );
        }
        this.grid.refresh();
        this.grid.clearSelection();
        const logItem = createLogItem(
          successfulCases.length > 1 ? {} : { caseNumber: successfulCases[0].caseNumber },
          AuditLogItems.ChangeAccession
        );
        logItem.comments = `Finalized specimens for case${
          successfulCases.length > 1
            ? `s ${successfulCases.map(e => e.caseNumber).join(", ")}`
            : ` ${successfulCases[0].caseNumber}`
        }.`;
        AuditLogApi.insertLogMessage(logItem);
      } catch (error) {
        handleErrors(error);
      } finally {
        this.isLoading = false;
        document.body.style.cursor = "";
      }
    },
    canFinalizeCase(data) {
      if (
        this.permissions.CaseFinalizeSlidePrep &&
        data.isSlidePrepOnly &&
        data.numberOfSpecimens > 0 &&
        ![
          CaseStatusEnum.SignedOnHold,
          CaseStatusEnum.ReportedPrelim,
          CaseStatusEnum.Signed,
          CaseStatusEnum.Reported,
          CaseStatusEnum.SignedAgain,
          CaseStatusEnum.ReReleased
        ].includes(data.caseStatus)
      ) {
        return true;
      }
      return false;
    },
    saveFilters({ component }) {
      const columns = component.getVisibleColumns().map(e => {
        return { name: e.name, filterValue: e?.filterValue || null };
      });
      this.$store.commit("sessionDetails/setDashboardFilters", { allCases: columns });
    }
  }
};
</script>

<style lang="scss" scoped>
/* The switch - the box around the slider */
.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: #2196f3;
}

input:focus + .slider {
  box-shadow: 0 0 1px #2196f3;
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

::v-deep .dx-pager {
  justify-content: flex-start;
  color: $primary-dark;
  font-weight: 600;
  font-size: 1rem;
  .dx-pages {
    margin: auto;
  }
}
.slider.round:before {
  border-radius: 50%;
}
::v-deep
  .dx-data-row.dx-state-hover:not(.dx-selection):not(.dx-row-inserted):not(.dx-row-removed):not(.dx-edit-row)
  > td:not(.dx-focused) {
  cursor: pointer;
}
</style>
